// Exit-intent quiz pop-up (snippets/quiz-popup.liquid). Opens a modal
// <dialog> once a visitor looks like they're leaving: on mouse/trackpad
// devices when the pointer leaves through the top of the window (towards the
// tabs or address bar), on touch devices when they quickly scroll back up.
//
// To avoid nagging, it opens at most once per visit and never when:
//   - it was shown in the last `data-frequency-days` days
//   - this is the quiz page itself
//   - the visit is younger than `data-min-seconds` / `data-min-pages`
//   - another modal (cart drawer, menu, Klaviyo form) is already open
//
// Test mode: add ?quiz_popup to any URL to open it straight away, ignoring
// all of the above.
if (!customElements.get('quiz-popup')) {
  customElements.define(
    'quiz-popup',
    class QuizPopup extends HTMLElement {
      static SEEN_KEY = 'quiz_popup_seen_at';
      static VISIT_KEY = 'quiz_popup_visit';
      static SCROLL_UP_WINDOW = 400; // ms in which the scroll-up has to happen
      static SCROLL_UP_DISTANCE = 0.3; // ...covering this share of the viewport

      connectedCallback() {
        this.dialog = this.querySelector('dialog');
        this.dialog.querySelector('.quiz-popup__close').addEventListener('click', () => this.dialog.close());
        this.dialog.querySelector('.quiz-popup__button')?.addEventListener('click', () => this.track('quiz_popup_clicked'));
        // A click on the backdrop lands on the <dialog> itself.
        this.dialog.addEventListener('click', (event) => {
          if (event.target === this.dialog) this.dialog.close();
        });

        if (this.dataset.preview !== undefined || new URLSearchParams(window.location.search).has('quiz_popup')) {
          this.open();
          return;
        }
        if (window.Shopify?.designMode) return;

        if (new URL(this.dataset.quizUrl, window.location.origin).pathname === window.location.pathname) return;

        this.visit = this.countPageView();
        if (this.isSnoozed()) return;

        if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
          this.onMouseOut = (event) => {
            if (!event.relatedTarget && event.clientY <= 0) this.attempt();
          };
          document.addEventListener('mouseout', this.onMouseOut);
        } else if (this.dataset.mobileTrigger === 'scroll_up') {
          this.lastY = this.maxY = this.anchorY = window.scrollY;
          this.anchorTime = performance.now();
          this.onScroll = () => this.checkScrollUp();
          window.addEventListener('scroll', this.onScroll, { passive: true });
        } else {
          return;
        }

        // The image sits in a closed dialog, so loading="lazy" would only
        // fetch it on open. Fetch it once the page itself is done instead.
        const img = this.dialog.querySelector('img');
        if (img) {
          if (document.readyState === 'complete') img.loading = 'eager';
          else window.addEventListener('load', () => (img.loading = 'eager'), { once: true });
        }
      }

      disconnectedCallback() {
        this.stopListening();
      }

      stopListening() {
        if (this.onMouseOut) document.removeEventListener('mouseout', this.onMouseOut);
        if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
      }

      // Fires on a fast upward flick after the visitor has scrolled at least
      // a screen down - a slow scroll back to re-read something doesn't count.
      checkScrollUp() {
        const y = window.scrollY;
        const now = performance.now();
        this.maxY = Math.max(this.maxY, y);

        if (y >= this.lastY || now - this.anchorTime > QuizPopup.SCROLL_UP_WINDOW) {
          this.anchorY = y;
          this.anchorTime = now;
        } else if (
          this.maxY > window.innerHeight &&
          this.anchorY - y > window.innerHeight * QuizPopup.SCROLL_UP_DISTANCE
        ) {
          this.attempt();
        }
        this.lastY = y;
      }

      attempt() {
        if (this.dialog.open || !this.isDue() || this.isOtherModalOpen()) return;

        this.stopListening();
        this.store(localStorage, QuizPopup.SEEN_KEY, String(Date.now()));
        this.open();
        this.track('quiz_popup_shown');
      }

      open() {
        this.dialog.querySelector('img')?.setAttribute('loading', 'eager');
        this.dialog.showModal();
        // showModal() focuses the first focusable element - the button - which
        // then shows a focus ring before anyone has touched the keyboard.
        // Focus the dialog itself; Tab still moves straight to the button.
        this.dialog.focus();
      }

      // Rules that can't change during this page view.
      isSnoozed() {
        const seenAt = parseInt(this.read(localStorage, QuizPopup.SEEN_KEY), 10) || 0;
        const days = parseInt(this.dataset.frequencyDays, 10) || 0;
        return Date.now() - seenAt < days * 24 * 60 * 60 * 1000;
      }

      // Rules that become true as the visit goes on.
      isDue() {
        const minSeconds = parseInt(this.dataset.minSeconds, 10) || 0;
        const minPages = parseInt(this.dataset.minPages, 10) || 1;
        return this.visit.pages >= minPages && Date.now() - this.visit.start >= minSeconds * 1000;
      }

      // Dawn's drawers and the Rebuy cart keep aria-modal="true" while closed
      // (just hidden, or aria-hidden and slid off-screen), so only count ones
      // actually on screen.
      isOtherModalOpen() {
        if (document.body.classList.contains('overflow-hidden')) return true;
        const modals = document.querySelectorAll('dialog[open], [aria-modal="true"]:not([aria-hidden="true"])');
        return Array.from(modals).some((element) =>
          element.checkVisibility
            ? element.checkVisibility({ visibilityProperty: true, opacityProperty: true })
            : element.getClientRects().length > 0
        );
      }

      // Visit (session) start time and page count, kept in sessionStorage.
      countPageView() {
        let visit;
        try {
          visit = JSON.parse(this.read(sessionStorage, QuizPopup.VISIT_KEY));
        } catch (e) {}
        if (!visit?.start) visit = { start: Date.now(), pages: 0 };
        visit.pages += 1;
        this.store(sessionStorage, QuizPopup.VISIT_KEY, JSON.stringify(visit));
        return visit;
      }

      // Storage can throw (private mode, blocked site data) - the pop-up then
      // just behaves as if nothing was stored.
      read(storage, key) {
        try {
          return storage.getItem(key);
        } catch (e) {
          return null;
        }
      }

      store(storage, key, value) {
        try {
          storage.setItem(key, value);
        } catch (e) {}
      }

      track(event) {
        if (typeof window.clarity === 'function') window.clarity('event', event);
      }
    }
  );
}
